import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { InputSlots, Keypad } from './DigitKeypad';

export function decompositionFields(question) {
  const fields = question?.answer_fields;
  if (question?.answer_mode !== 'decomposition_entry' || !Array.isArray(fields) || fields.length < 1 || fields.length > 12) return null;
  return fields.every((field) => Number.isInteger(field.length) && field.length >= 1 && field.length <= 12 && /^(?:·10[⁻⁰¹²³⁴⁵⁶⁷⁸⁹]+)?$/.test(field.suffix ?? '')) ? fields : null;
}

function answerValues(answer, fields) {
  const terms = String(answer ?? '').split(' + ');
  return fields.map((field, index) => {
    const suffix = field.suffix || '';
    const term = terms[index] || '';
    if (suffix && !term.endsWith(suffix)) return '';
    const digits = suffix ? term.slice(0, -suffix.length) : term;
    return new RegExp(`^[0-9]{${field.length}}$`).test(digits) ? digits : '';
  });
}

export default function DecompositionAnswerInput({ fields, disabled, qaMode, reveal, correctAnswer, onAnswer }) {
  const [values, setValues] = useState(() => fields.map(() => ''));
  const [active, setActive] = useState(0);
  const sent = useRef(false);
  const locked = disabled || qaMode;
  const complete = values.every((value, index) => value.length === fields[index].length);
  const displayed = qaMode || reveal ? answerValues(correctAnswer, fields) : values;
  const add = (digit) => {
    if (locked || values[active].length >= fields[active].length) return;
    const next = [...values];
    next[active] += digit;
    setValues(next);
    if (next[active].length === fields[active].length && active < fields.length - 1) setActive(active + 1);
  };
  const remove = () => {
    if (locked) return;
    const index = !values[active] && active > 0 ? active - 1 : active;
    const next = [...values];
    next[index] = next[index].slice(0, -1);
    setValues(next);
    setActive(index);
  };
  const submit = () => {
    if (locked || !complete || sent.current) return;
    sent.current = true;
    onAnswer(values.map((value, index) => value + (fields[index].suffix || '')).join(' + '));
  };
  useEffect(() => { if (!disabled) sent.current = false; }, [disabled]);
  useEffect(() => {
    const handle = (event) => {
      if (locked || event.ctrlKey || event.metaKey || event.altKey || /INPUT|TEXTAREA/.test(event.target?.tagName || '') || event.target?.isContentEditable) return;
      if (/^[0-9]$/.test(event.key)) { event.preventDefault(); add(event.key); }
      else if (event.key === 'Backspace') { event.preventDefault(); remove(); }
      else if (event.key === 'Enter' && event.target?.tagName !== 'BUTTON') { event.preventDefault(); submit(); }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  });
  return <Box sx={{ width: '100%', my: 1 }}>
    <Typography sx={{ textAlign: 'center', fontWeight: 800, color: '#4c1d95', mb: 1 }}>{qaMode ? 'Doğru cevap' : 'Kutucukları sırayla doldur.'}</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
      {fields.map((field, index) => <React.Fragment key={index}>
        {index > 0 && <Typography aria-hidden sx={{ fontSize: { xs: 26, md: 36 }, fontWeight: 900 }}>+</Typography>}
        <ButtonBase disabled={locked} onClick={() => setActive(index)} aria-label={`${index + 1}. terimi düzenle`}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '2px solid', borderColor: reveal || qaMode ? '#22c55e' : active === index ? '#8b5cf6' : 'transparent', borderRadius: 2, p: 0.5, '&:focus-visible': { outline: '3px solid #a78bfa' } }}>
          <Typography sx={{ fontSize: 13, color: '#6d5d93' }}>{field.label || `${index + 1}. terim`}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <InputSlots compact length={field.length} value={displayed[index]} />
            {field.suffix && <Typography sx={{ fontSize: { xs: 22, md: 32 }, fontWeight: 800, color: '#172554' }}>{field.suffix}</Typography>}
          </Box>
        </ButtonBase>
      </React.Fragment>)}
    </Box>
    <Keypad disabled={locked} onDigit={add} onDelete={remove} onSubmit={submit} submitDisabled={!complete} submitLabel="Kontrol et" />
  </Box>;
}
